import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';

interface SearchUser {
  id: string;
  user_id: string;
  username: string;
  name: string;
  email: string;
  description: string;
  role: string;
  profileComplete: boolean;
  profile_image_url?: string;
  account_status?: string;
  created_at: string;
  updated_at: string;
}

const UserSearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });


  const performSearch = useCallback(async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/users/search', {
        params: {
          q: query.trim(),
          page,
          limit: pagination.limit
        }
      });

      // Access the nested data from the API response
      const apiResponse = response.data;
      const data = apiResponse.data;

      setSearchResults(data.users || []);
      setPagination(apiResponse.metadata?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      setHasSearched(true);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery, 1);
  };

  const handlePageChange = (newPage: number) => {
    performSearch(searchQuery, newPage);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
  };



  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>Find Speakers</h1>
              <p className="text-muted mb-0">
                Search for speakers and event organizers by name or email
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSearchSubmit}>
                <div className="row">
                  <div className="col-md-10">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or email address..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleClearSearch}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={isLoading || !searchQuery.trim()}
                    >
                      {isLoading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        'Search'
                      )}
                    </button>
                  </div>
                </div>
              </form>

              {error && (
                <div className="alert alert-danger mt-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Error:</strong> {error}
                  <div className="mt-2">
                    <small className="text-muted">
                      Please try again or contact support if the problem persists.
                    </small>
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      </div>

      {hasSearched && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  Search Results
                  {pagination.total > 0 && (
                    <span className="badge bg-primary ms-2">{pagination.total}</span>
                  )}
                </h5>
                {searchQuery && (
                  <small className="text-muted">
                    Searching for: "{searchQuery}"
                  </small>
                )}
              </div>
              <div className="card-body">
                {searchResults.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-4x text-muted mb-4"></i>
                    <h4 className="text-muted">No users found</h4>
                    <p className="text-muted mb-4">
                      We couldn't find any users matching "{searchQuery}".
                    </p>
                    <div className="row justify-content-center">
                      <div className="col-md-8">
                        <div className="card bg-light">
                          <div className="card-body">
                            <h6 className="card-title">
                              <i className="fas fa-lightbulb me-2"></i>
                              Try these suggestions:
                            </h6>
                            <ul className="list-unstyled mb-0 text-start">
                              <li><i className="fas fa-check text-success me-2"></i>Check your spelling</li>
                              <li><i className="fas fa-check text-success me-2"></i>Try searching with partial names</li>
                              <li><i className="fas fa-check text-success me-2"></i>Search by email address instead</li>
                              <li><i className="fas fa-check text-success me-2"></i>Use fewer or different keywords</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row">
                      {searchResults.map((user) => (
                        <div key={user.id} className="col-lg-6 mb-4">
                          <div className="card h-100 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-start flex-grow-1">
                                  {/* Profile Image */}
                                  <div className="me-3">
                                    {user.profile_image_url ? (
                                      <img
                                        src={user.profile_image_url}
                                        alt={user.name || 'User'}
                                        className="rounded-circle"
                                        style={{
                                          width: '60px',
                                          height: '60px',
                                          objectFit: 'cover',
                                          border: '2px solid #dee2e6'
                                        }}
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                        style={{
                                          width: '60px',
                                          height: '60px',
                                          border: '2px solid #dee2e6'
                                        }}
                                      >
                                        <i className="fas fa-user fa-2x text-muted"></i>
                                      </div>
                                    )}
                                  </div>

                                  {/* User Info */}
                                  <div className="flex-grow-1">
                                    <h6 className="card-title mb-1 fw-bold">
                                      {user.name || 'No name provided'}
                                    </h6>
                                    <p className="text-muted small mb-0">
                                      <i className="fas fa-envelope me-1"></i>
                                      {user.email}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-end">
                                  <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-primary'} mb-2`}>
                                    {user.role === 'admin' ? (
                                      <>
                                        <i className="fas fa-crown me-1"></i>
                                        ADMIN
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-user me-1"></i>
                                        USER
                                      </>
                                    )}
                                  </span>
                                  <br />
                                  {user.profileComplete ? (
                                    <span className="badge bg-success">
                                      <i className="fas fa-check-circle me-1"></i>
                                      Complete
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">
                                      <i className="fas fa-exclamation-circle me-1"></i>
                                      Incomplete
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Account Status Indicator */}
                              {(user as any).account_status === 'locked' && (
                                <div className="mb-3">
                                  <div className="alert alert-danger py-2">
                                    <i className="fas fa-lock me-2"></i>
                                    <strong>Account Locked</strong>
                                    <div className="small mt-1">
                                      This account has been locked by an administrator.
                                    </div>
                                  </div>
                                </div>
                              )}

                              {user.description ? (
                                <div className="mb-3">
                                  <p className="card-text small text-muted">
                                    <i className="fas fa-quote-left me-1"></i>
                                    {user.description.length > 120
                                      ? `${user.description.substring(0, 120)}...`
                                      : user.description
                                    }
                                  </p>
                                </div>
                              ) : (
                                <div className="mb-3">
                                  <p className="card-text small text-muted fst-italic">
                                    <i className="fas fa-info-circle me-1"></i>
                                    No description provided
                                  </p>
                                </div>
                              )}

                              <div className="d-flex justify-content-between align-items-center">
                                <div className="text-muted small">
                                  <i className="fas fa-calendar-alt me-1"></i>
                                  Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                                </div>
                                <Link
                                  to={`/users/${user.id}`}
                                  className="btn btn-outline-primary btn-sm"
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  View Profile
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <nav aria-label="Search results pagination" className="mt-4">
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${!pagination.hasPrev ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={!pagination.hasPrev}
                            >
                              <i className="fas fa-chevron-left"></i>
                              Previous
                            </button>
                          </li>

                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            const pageNum = Math.max(1, pagination.page - 2) + i;
                            if (pageNum <= pagination.totalPages) {
                              return (
                                <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                </li>
                              );
                            }
                            return null;
                          })}

                          <li className={`page-item ${!pagination.hasNext ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={!pagination.hasNext}
                            >
                              Next
                              <i className="fas fa-chevron-right ms-1"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}

                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                        {pagination.total} results
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!hasSearched && !searchQuery && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-users fa-4x text-muted mb-4"></i>
                <h4 className="text-muted">Find Conference Speakers</h4>
                <p className="text-muted mb-4">
                  Search for speakers and event organizers to connect with the conference community.
                </p>
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-title">
                          <i className="fas fa-lightbulb me-2"></i>
                          Search Tips
                        </h6>
                        <ul className="list-unstyled mb-0 text-start">
                          <li><i className="fas fa-check text-success me-2"></i>Search by full name or partial name</li>
                          <li><i className="fas fa-check text-success me-2"></i>Search by email address</li>
                          <li><i className="fas fa-check text-success me-2"></i>Results update as you type</li>
                          <li><i className="fas fa-check text-success me-2"></i>View detailed profiles and contact information</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserSearchPage;
